import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAudioStreamProps {
  socket: any;
  onError?: (error: Error) => void;
  onPlaying?: () => void;
  onStopped?: () => void;
}

interface AudioState {
  isPlaying: boolean;
  userId: string | null;
}

export function useAudioStream({
  socket,
  onError,
  onPlaying,
  onStopped,
}: UseAudioStreamProps) {
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const currentStreamIdRef = useRef<string | null>(null);
  const chunksRef = useRef<Uint8Array[]>([]);
  const [audioState, setAudioState] = useState<AudioState>({
    isPlaying: false,
    userId: null,
  });

  const playBlob = useCallback(
    async (blob: Blob) => {
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.src = '';
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
      const audio = new Audio();
      audioElementRef.current = audio;
      audio.src = URL.createObjectURL(blob);
      audio
        .play()
        .then(() => {
          setAudioState((prev) => ({ ...prev, isPlaying: true }));
          onPlaying?.();
        })
        .catch((err) => {
          onError?.(err as Error);
        });
      audio.onended = () => {
        stop();
      };
    },
    [onError, onPlaying],
  );

  const stop = useCallback(() => {
    if (audioElementRef.current) {
      if (audioElementRef.current.src) {
        URL.revokeObjectURL(audioElementRef.current.src);
      }
      audioElementRef.current.pause();
      audioElementRef.current.src = '';
    }
    currentStreamIdRef.current = null;
    chunksRef.current = [];
    setAudioState({
      isPlaying: false,
      userId: null,
    });
    onStopped?.();
  }, [onStopped]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const handleAudioChunk = (data: {
      streamId: string;
      userId: string;
      chunk: string;
      format: string;
      isLastChunk: boolean;
    }) => {
      if (!currentStreamIdRef.current && data.userId) {
        currentStreamIdRef.current = data.streamId;
        setAudioState({
          isPlaying: false,
          userId: data.userId,
        });
        chunksRef.current = [];
      }
      if (data.streamId !== currentStreamIdRef.current) return;

      if (data.chunk) {
        const binaryString = atob(data.chunk);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        chunksRef.current.push(bytes);
      }

      if (data.isLastChunk) {
        const totalLength = chunksRef.current.reduce(
          (acc, arr) => acc + arr.length,
          0,
        );
        const full = new Uint8Array(totalLength);
        let offset = 0;
        for (const arr of chunksRef.current) {
          full.set(arr, offset);
          offset += arr.length;
        }
        const blob = new Blob([full], { type: 'audio/mp3' });
        playBlob(blob);
        chunksRef.current = [];
      }
    };

    const handleAudioStop = () => {
      stop();
    };

    socket.on('/voice-channel/ai/answer', handleAudioChunk);
    socket.on('/voice-channel/ai/answer/stop', handleAudioStop);

    return () => {
      socket.off('/voice-channel/ai/answer', handleAudioChunk);
      socket.off('/voice-channel/ai/answer/stop', handleAudioStop);
      stop();
    };
  }, []);

  return {
    stop,
    audioState,
  };
}
